import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical, faXmark } from "@fortawesome/free-solid-svg-icons";
import { putTemplateLessonOrder, type TeacherLessonRecord } from "../auth/api-client";
import { LessonIcon } from "./LessonIcon";
import { SequenceInsertSlot } from "./SequenceInsertSlot";

function lessonIdsInOrder(items: readonly TeacherLessonRecord[]): string[] {
  return items.map((l) => l.id);
}

function ordersMatch(a: readonly TeacherLessonRecord[], b: readonly TeacherLessonRecord[]): boolean {
  const aIds = lessonIdsInOrder(a);
  const bIds = lessonIdsInOrder(b);
  return aIds.length === bIds.length && aIds.every((id, i) => id === bIds[i]);
}

/** Move item at `fromIndex` so it sits immediately before `insertBeforeIndex` (0 … length). */
export function reorderLessonList<T>(
  list: readonly T[],
  fromIndex: number,
  insertBeforeIndex: number
): T[] {
  if (fromIndex < 0 || fromIndex >= list.length) return [...list];
  if (insertBeforeIndex < 0 || insertBeforeIndex > list.length) return [...list];
  if (fromIndex === insertBeforeIndex || fromIndex + 1 === insertBeforeIndex) {
    return [...list];
  }
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  if (moved === undefined) return [...list];
  let insertAt = insertBeforeIndex;
  if (fromIndex < insertAt) insertAt -= 1;
  next.splice(insertAt, 0, moved);
  return next;
}

function insertAfterLessonIdForGap(
  lessons: readonly TeacherLessonRecord[],
  insertBeforeIndex: number
): string | null | undefined {
  if (insertBeforeIndex === 0) return null;
  if (insertBeforeIndex >= lessons.length) return undefined;
  return lessons[insertBeforeIndex - 1]?.id;
}

export interface TemplateLessonSequenceRailProps {
  readonly courseId: string;
  readonly lessons: readonly TeacherLessonRecord[];
  readonly archived: boolean;
  readonly loading: boolean;
  readonly bearer: string | null;
  readonly onOrderSaved: () => Promise<void>;
  readonly onError: (message: string | null) => void;
  readonly onRemove: (lessonId: string, title: string) => void;
  readonly onInsert?: (insertAfterLessonId: string | null | undefined) => void;
  /** When true, defer API save until parent confirms a changelog via onPendingOrderSave. */
  readonly requireChangelog?: boolean;
  readonly onPendingOrderSave?: (nextOrder: readonly TeacherLessonRecord[]) => void;
  /** Bump to revert local order to the latest `lessons` prop (e.g. after canceling changelog). */
  readonly resetOrderSignal?: number;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function TemplateLessonSequenceRail({
  courseId,
  lessons,
  archived,
  loading,
  bearer,
  onOrderSaved,
  onError,
  onRemove,
  onInsert,
  requireChangelog = false,
  onPendingOrderSave,
  resetOrderSignal = 0,
}: TemplateLessonSequenceRailProps) {
  const [orderedLessons, setOrderedLessons] = useState<TeacherLessonRecord[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [insertBeforeIndex, setInsertBeforeIndex] = useState<number | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setOrderedLessons([...lessons]);
  }, [lessons, resetOrderSignal]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const orderDirty = useMemo(
    () => orderedLessons.length > 0 && !ordersMatch(orderedLessons, lessons),
    [orderedLessons, lessons]
  );

  const persistOrder = useCallback(
    async (nextOrder: TeacherLessonRecord[]) => {
      if (!bearer || ordersMatch(nextOrder, lessons)) return;
      if (requireChangelog && onPendingOrderSave) {
        onPendingOrderSave(nextOrder);
        return;
      }
      setSaveStatus("saving");
      onError(null);
      try {
        await putTemplateLessonOrder(bearer, courseId, lessonIdsInOrder(nextOrder));
        await onOrderSaved();
        setSaveStatus("saved");
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 1800);
      } catch (err) {
        setSaveStatus("error");
        onError(err instanceof Error ? err.message : "Save order failed");
        setOrderedLessons([...lessons]);
      }
    },
    [bearer, courseId, lessons, onError, onOrderSaved, onPendingOrderSave, requireChangelog]
  );

  const applyInsert = useCallback(
    (fromIndex: number, beforeIndex: number) => {
      const next = reorderLessonList(orderedLessons, fromIndex, beforeIndex);
      setOrderedLessons(next);
      if (!ordersMatch(next, lessons)) {
        void persistOrder(next);
      }
    },
    [lessons, orderedLessons, persistOrder]
  );

  const handleDragStart = (index: number, e: React.DragEvent) => {
    setDragFromIndex(index);
    setInsertBeforeIndex(null);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragEnd = () => {
    setDragFromIndex(null);
    setInsertBeforeIndex(null);
  };

  const handleDropGap = (beforeIndex: number) => {
    if (dragFromIndex === null) return;
    applyInsert(dragFromIndex, beforeIndex);
    handleDragEnd();
  };

  const renderGap = (insertBeforeIndexValue: number, insertLabel: string) => {
    const reorderEnabled = !archived && saveStatus !== "saving";
    const isDragging = dragFromIndex !== null;
    return (
      <SequenceInsertSlot
        key={`gap-${insertBeforeIndexValue}`}
        disabled={archived || !onInsert}
        label={insertLabel}
        onInsert={
          onInsert
            ? () => onInsert(insertAfterLessonIdForGap(orderedLessons, insertBeforeIndexValue))
            : undefined
        }
        isDragging={isDragging}
        drag={
          reorderEnabled
            ? {
                insertBeforeIndex: insertBeforeIndexValue,
                isActive: insertBeforeIndex === insertBeforeIndexValue,
                onDragOverGap: setInsertBeforeIndex,
                onDropGap: handleDropGap,
              }
            : undefined
        }
      />
    );
  };

  if (loading) {
    return <p className="muted teacher-sequence-empty">Loading sequence…</p>;
  }

  if (orderedLessons.length === 0) {
    return (
      <div className="teacher-sequence-board">
        <div className="teacher-sequence-empty teacher-sequence-empty--hero">
          <p className="teacher-sequence-empty-title">Your lesson path starts here</p>
          <p className="muted">Hover below to add your first lesson, then drag cards to shape the journey.</p>
        </div>
        {!archived && onInsert ? (
          <ol className="teacher-sequence-list">
            {renderGap(0, "Add first lesson")}
          </ol>
        ) : null}
      </div>
    );
  }

  const reorderEnabled = !archived && saveStatus !== "saving";
  const isDragging = dragFromIndex !== null;

  return (
    <div className="teacher-sequence-board">
      {!archived ? (
        <div className="teacher-sequence-board-toolbar">
          <p className="teacher-sequence-drag-hint">
            Grab a card and drop it on a glowing slot. Order saves automatically. Hover between lessons to add more.
          </p>
          {saveStatus === "saving" ? (
            <span className="teacher-sequence-save-pill is-saving" role="status">
              Saving order…
            </span>
          ) : saveStatus === "saved" ? (
            <span className="teacher-sequence-save-pill is-saved" role="status">
              Order saved
            </span>
          ) : saveStatus === "error" || orderDirty ? (
            <span className="teacher-sequence-save-pill is-error" role="status">
              Couldn&apos;t save. Try dragging again
            </span>
          ) : null}
        </div>
      ) : null}
      <ol
        className={`teacher-sequence-list${isDragging ? " is-dragging" : ""}`}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setInsertBeforeIndex(null);
          }
        }}
      >
        {orderedLessons.map((l, index) => (
          <Fragment key={l.id}>
            {renderGap(index, index === 0 ? "Add lesson at start" : `Add lesson before ${l.title}`)}
            <li
              className={`teacher-sequence-card${dragFromIndex === index ? " is-source" : ""}${
                isDragging && dragFromIndex !== index ? " is-idle-during-drag" : ""
              }`}
            >
              {!archived ? (
                <div
                  className="teacher-sequence-card-grip"
                  draggable={reorderEnabled}
                  aria-label={`Drag to reorder ${l.title}`}
                  title="Drag to reorder"
                  onDragStart={(e) => handleDragStart(index, e)}
                  onDragEnd={handleDragEnd}
                >
                  <FontAwesomeIcon icon={faGripVertical} fixedWidth />
                </div>
              ) : null}
              <span className="teacher-sequence-card-step" aria-hidden>
                {index + 1}
              </span>
              <LessonIcon
                lessonId={l.id}
                title={l.title}
                iconName={l.iconName}
                bgColor={l.iconBgColor}
                fgColor={l.iconFgColor}
                workspaceMode={l.workspaceMode}
                size={3.25}
                className="teacher-sequence-card-icon"
              />
              <div className="teacher-sequence-card-body">
                <span className="teacher-sequence-card-title">{l.title}</span>
                {l.description ? (
                  <span className="teacher-sequence-card-desc">{l.description}</span>
                ) : (
                  <span className="teacher-sequence-card-desc is-placeholder">No description yet</span>
                )}
              </div>
              {!archived ? (
                <button
                  type="button"
                  className="teacher-sequence-card-remove"
                  disabled={saveStatus === "saving"}
                  onClick={() => onRemove(l.id, l.title)}
                  title="Remove from template"
                  aria-label={`Remove ${l.title} from sequence`}
                >
                  <FontAwesomeIcon icon={faXmark} fixedWidth />
                </button>
              ) : null}
            </li>
          </Fragment>
        ))}
        {renderGap(
          orderedLessons.length,
          orderedLessons.length === 0 ? "Add first lesson" : "Add lesson at end"
        )}
      </ol>
    </div>
  );
}
